import React, { useState } from 'react';
import { Menu, MenuItemProps, Tooltip, Box, Circle } from '@televet/kibble-ui';
import { ServiceWhereInput } from 'shared/types/graphql';
import remove from 'lodash-es/remove';

enum filterGroups {
  Status = 'Status',
  Delivery = 'Delivery',
}
enum statusFilters {
  Active = 'Active',
  Inactive = 'Inactive',
  Scheduled = 'Scheduled',
}
enum deliveryFilters {
  Email = 'Email',
  PetPortal = 'Pet Portal',
  TextMessage = 'Text Message',
  Postcard = 'Postcard',
}

const statusFilterMenuItems: MenuItemProps[] = Object.values(statusFilters).map((statusFilter) => {
  return {
    groupLabel: filterGroups.Status,
    label: statusFilter,
    value: statusFilter,
  };
});

const deliveryFilterMenuItems: MenuItemProps[] = Object.values(deliveryFilters).map((deliveryFilter) => {
  return {
    groupLabel: filterGroups.Delivery,
    label: deliveryFilter,
    value: deliveryFilter,
  };
});

interface ServiceFilterButtonProps {
  handleFilterChange: (filter: ServiceWhereInput) => void;
  showPostcards: boolean;
}

const ServiceFilterButton = ({ handleFilterChange, showPostcards }: ServiceFilterButtonProps): JSX.Element => {
  const [hasSelectedItem, setHasSelectedItem] = useState(false);

  if (!showPostcards)
    remove(deliveryFilterMenuItems, (filterMenuItem) => filterMenuItem.value === deliveryFilters.Postcard);

  const updateFilters = (selectedItems: MenuItemProps[]): void => {
    setHasSelectedItem(!!selectedItems.length);

    const statuses = selectedItems.filter((menuItem) => menuItem.groupLabel === filterGroups.Status);
    const deliveryOptions = selectedItems.filter((menuItem) => menuItem.groupLabel === filterGroups.Delivery);

    const statusInput: ServiceWhereInput[] = [];
    statuses?.forEach(({ value }) => {
      const now = new Date();
      switch (value) {
        case statusFilters.Active:
          statusInput.push({
            reminderActivationDate: { lte: now },
          });
          break;
        case statusFilters.Inactive:
          statusInput.push({
            reminderActivationDate: { equals: null },
          });
          break;
        case statusFilters.Scheduled:
          statusInput.push({
            reminderActivationDate: { gt: now },
          });
          break;
        default:
          break;
      }
    });

    const deliveryInput: ServiceWhereInput[] = [];
    deliveryOptions?.forEach(({ value }) => {
      if (value === deliveryFilters.Email) {
        deliveryInput.push({
          reminderTimings: {
            some: {
              isDeleted: { equals: false },
              isEmail: { equals: true },
            },
          },
        });
      }
      if (value === deliveryFilters.PetPortal) {
        deliveryInput.push({
          isPetPortal: { equals: true },
        });
      }
      if (value === deliveryFilters.TextMessage) {
        deliveryInput.push({
          reminderTimings: {
            some: {
              isDeleted: { equals: false },
              isSms: { equals: true },
            },
          },
        });
      }
      if (value === deliveryFilters.Postcard) {
        deliveryInput.push({
          isPostcard: { equals: true },
        });
      }
    });

    const newFilter = { AND: [] as ServiceWhereInput[] };
    if (statusInput.length) {
      newFilter.AND.push({ OR: statusInput });
    }
    if (deliveryInput.length) {
      newFilter.AND.push({ OR: deliveryInput });
    }
    handleFilterChange(newFilter);
  };

  const onSelect = (_selectedItem: MenuItemProps, allSelectedItems: MenuItemProps[] | undefined): void =>
    updateFilters(allSelectedItems || []);

  return (
    <Tooltip label="Filters">
      <Box position="relative">
        {hasSelectedItem && (
          <Circle
            position="absolute"
            top="6px"
            right="6px"
            size="12px"
            border="2px solid #fff"
            bgColor="background.primary"
            zIndex={10}
          />
        )}
        <Menu
          buttonProps={{
            leftIconName: 'filter',
            showRightIcon: false,
            size: 'md',
            variant: 'secondarySubtle',
          }}
          listProps={{
            overflowY: 'auto',
            canSelectAll: true,
            canSelectGroups: false,
            isGrouped: true,
            isMultiSelect: true,
            isSearchable: false,
            onSelect: onSelect,
            onSelectAll: updateFilters,
            menuItems: [...statusFilterMenuItems, ...deliveryFilterMenuItems],
          }}
        />
      </Box>
    </Tooltip>
  );
};

export default React.memo(ServiceFilterButton);
